import React, { useState, useEffect, useCallback } from 'react';
import {
  useFetchAlertRecipient,
  useFetchSingleShipment,
  useUnAuthorize,
} from '@/hooks';
import { CustomModalWithoutButton } from '../../../components';
import { useToken } from '@/common/helper';
import ShipmentNotificationModal from './ShipmentNotificationModal';
import OneTimeNotificationModal from './OneTimeNotificationModal';
import type { SingleShipmentDataExtended } from '@/apis/types';
import type { AlertRecipient, NotificationBulkScope } from '@/apis/shipment';
import { NotificationData } from './OneTimeNotificationModal';

interface PerShipmentNotificationModalProps {
  selected: NotificationData;
  shipmentId: string;
  notificationModalType: number;
  fetchNotifications?: (showSpinner: boolean) => void;
  openNotificationModal: boolean;
  setOpenNotificationModal: (open: boolean) => void;
  isMultiple?: boolean;
  notificationId?: string | null;
  onNotificationCreated?: () => void;
  bulkScope?: NotificationBulkScope | null;
}

/**
 * Modal component for managing shipment notifications
 *
 * Displays either a ShipmentNotificationModal (for alert notifications)
 * or OneTimeNotificationModal (for one-time messages) based on the
 * notificationModalType prop. Handles data fetching for shipment details
 * and alert recipients.
 *
 * @param selected - Selected notification data (passed to child modal)
 * @param shipmentId - ID of the shipment
 * @param notificationModalType - Type of notification (1 for shipment alert, other for one-time)
 * @param fetchNotifications - Callback to refresh notifications list
 * @param openNotificationModal - Whether the modal is open
 * @param setOpenNotificationModal - Function to control modal visibility
 * @param isMultiple - Whether operating on multiple shipments
 */
const PerShipmentNotificationModal: React.FC<
  PerShipmentNotificationModalProps
> = ({
  selected,
  shipmentId,
  notificationModalType,
  fetchNotifications,
  openNotificationModal,
  setOpenNotificationModal,
  isMultiple = false,
  notificationId = null,
  onNotificationCreated,
  bulkScope = null,
}) => {
  const { recipientData } = useFetchAlertRecipient(
    shipmentId,
    isMultiple,
    bulkScope
  );
  const { shipmentData } = useFetchSingleShipment(
    isMultiple ? undefined : shipmentId,
    false
  );
  const [shipment, setShipment] = useState<SingleShipmentDataExtended | null>(
    null
  );
  const { checkTokenExpiry, decodedUserToken } = useToken();
  const loggedInUserId = decodedUserToken()?.email;
  const { unauthorize } = useUnAuthorize();
  const [recipient, setRecipient] = useState<AlertRecipient[] | null>(null);
  const [step, setStep] = useState<number>(1);
  // one-time modal wants a non-null token; the interceptor ignores it and reads the active token anyway
  const getTokenForOneTime = useCallback(
    () => checkTokenExpiry() ?? '',
    [checkTokenExpiry]
  );

  useEffect(() => {
    if (shipmentData) {
      setShipment(shipmentData);
    }
  }, [shipmentData]);

  useEffect(() => {
    if (recipientData) {
      setRecipient(recipientData);
    }
  }, [recipientData]);

  const onSuccess = (): void => {
    fetchNotifications?.(false);
    setOpenNotificationModal(false);
  };

  return (
    <>
      <CustomModalWithoutButton
        lineHeight='28px'
        fontSize='20px'
        modalSize={600}
        modalTitle={
          notificationModalType === 1
            ? step === 3
              ? 'Add alert types'
              : 'Shipment notification'
            : 'One-time message'
        }
        modalOpen={openNotificationModal}
        setModalOpen={setOpenNotificationModal}
      >
        {notificationModalType === 1 ? (
          <ShipmentNotificationModal
            selected={selected}
            step={step}
            setStep={setStep}
            onSuccess={onSuccess}
            shipment={shipment}
            unauthorize={unauthorize}
            checkTokenExpiry={checkTokenExpiry}
            openNotificationModal={openNotificationModal}
            setOpenNotificationModal={setOpenNotificationModal}
            recipientData={recipient}
            loggedInUserId={loggedInUserId}
            shipmentId={shipmentId}
            isMultiple={isMultiple}
            onNotificationCreated={onNotificationCreated}
            bulkScope={bulkScope}
          />
        ) : (
          <OneTimeNotificationModal
            onSuccess={onSuccess}
            shipment={shipment ?? undefined}
            recipientData={recipient ?? undefined}
            loggedInUserId={loggedInUserId}
            unauthorize={unauthorize}
            checkTokenExpiry={getTokenForOneTime}
            setOpenNotificationModal={setOpenNotificationModal}
            shipmentId={shipmentId}
            isMultiple={isMultiple}
            notificationId={notificationId ?? undefined}
            onNotificationCreated={onNotificationCreated}
            bulkScope={bulkScope}
          />
        )}
      </CustomModalWithoutButton>
    </>
  );
};

export default PerShipmentNotificationModal;
